import { useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import useUser from '@/hooks/useUser.js';
import useChangeCompany from '@/hooks/useChangeCompany.js';
import useCompanyId from '@/hooks/useCompanyId.js';
import { getUserCompaniesOptions } from '@/redux/actions/userActions.js';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import selectors from '@/redux/selectors/selectors.js';
import IconV2 from '../v2/IconV2.jsx';
import { FiChevronDown } from '../react-icons/main';
import Stack from '../v3/Stack.jsx';
import CompanyPopover from '../CompanyPopover.jsx';
import useDebouncedValue from '@/hooks/useDebouncedValue.js';
import setPref from '@/redux/actions/uiActions/setPref.js';
import { store } from '@/redux/lib/configureStore.js';
import Spinner from '../Spinner.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const MAX_RECENT_COMPANIES = 6;

/**
 * Updates the list of recently selected companies in preferences
 */
const updateRecentCompanies = (company) => {
  const currentState = store.getState();
  const recentCompanies = currentState?.ui?.prefs?.recentCompanies;

  const newRecentCompanies = [{ ...company, timestamp: Date.now() }, ...(recentCompanies || []).filter((c) => c.id !== company.id)].slice(0, MAX_RECENT_COMPANIES);

  setPref('recentCompanies', newRecentCompanies);
};

/**
 * Company selector component with search and recent companies functionality
 */
const PlatformCompanySelect = ({ active }) => {
  const [search, setSearch] = useState('');
  const [isChangingCompany, setIsChangingCompany] = useState(false);
  const [changingCompanyName, setChangingCompanyName] = useState('');
  const debouncedSearch = useDebouncedValue(search, 200);
  const lastDebouncedSearch = useRef(debouncedSearch);
  const cancelRef = useRef();

  const user = useUser();
  const { companyId, setSessionCompanyId } = useCompanyId();
  const userId = useSelector(selectors.userId);
  const includeUserSearch = useArgSelector(selectors.pref, 'showPersonal', (value) => value === 'true' || value === true);

  // Filter out deleted companies
  const userCompanies = useSelector(selectors.userCompaniesOptions)?.filter((company) => company?.isDeleted !== true) || [];

  const changeCompany = useChangeCompany();
  const selectedCompany = useSelector(selectors.userSelectedCompany) || {};
  const isAdmin = user?.jwt?.admin;

  // Format company options for the dropdown
  const companyOptions = userCompanies.map((company) => ({
    value: company.id,
    label: company?.isPersonal && !company?.name ? 'Personal account' : company.name,
    ...company
  }));

  // Get and filter recent companies
  const recentCompanies = useArgSelector(selectors.pref, 'recentCompanies')
    ?.filter((company) => company?.id !== selectedCompany?.id)
    ?.filter((company) => !company?.isPersonal || includeUserSearch);
  const recentCompanyIds = recentCompanies?.map((company) => company.id)?.join(',');
  const selectedCompanyName = selectedCompany?.isPersonal //
    ? isAdmin && selectedCompany?.userId !== userId
      ? `${selectedCompany.username} (Personal)`
      : 'Personal Account'
    : selectedCompany.name || 'Select';

  const handleCreateNewOrganization = () => {
    setModalState('createOrganization', { open: true });
  };

  const handleToggleShowPersonal = () => {
    const currentValue = store.getState()?.ui?.prefs?.showPersonal;
    const isCurrentlyEnabled = currentValue === 'true' || currentValue === true;
    setPref('showPersonal', (!isCurrentlyEnabled).toString());
  };

  const handleCompanyChange = async (company) => {
    await waitForSyncReady();
    updateRecentCompanies(company);
    setIsChangingCompany(true);
    setChangingCompanyName(company.name);
    setSessionCompanyId(company.id);
    changeCompany(company.id, { message: 'Please wait while we change companies...', redirect: true }, async () => {
      setIsChangingCompany(false);
      await getUserCompaniesOptions({ userId });
      setChangingCompanyName('');
    });
  };

  const handleSearch = (event) => setSearch(event.target.value);

  // Fetch companies when search changes or when companyId is set
  useEffect(() => {
    // Always fetch when we have userId and companyId
    // This ensures we get the selectedCompany data on initial load
    if (userId && companyId) {
      getUserCompaniesOptions({ userId, search: encodeURIComponent(debouncedSearch), includeUserSearch, recentCompanyIds, cancelRef });
    }
  }, [userId, debouncedSearch, includeUserSearch, recentCompanyIds, companyId]);

  useEffect(() => {
    lastDebouncedSearch.current = debouncedSearch;
  }, [debouncedSearch]);

  return (
    <div className={`mc-company-select ${active ? 'active' : ''}`} data-title={'Switch organizations'}>
      <CompanyPopover //
        isAdmin={isAdmin}
        debouncedSearch={debouncedSearch}
        options={companyOptions}
        selectedCompany={selectedCompany}
        recentCompanies={recentCompanies}
        handleCreateNewOrganization={handleCreateNewOrganization}
        onSearch={handleSearch}
        search={search}
        onChangeCompany={handleCompanyChange}
        toggleShowPersonal={handleToggleShowPersonal}
        showPersonal={includeUserSearch}>
        <Stack>
          <div className={'label'}>{changingCompanyName || selectedCompanyName}</div>
          <div role={'button'} className={'mc-breadcrumb-select'}>
            <IconV2 icon={isChangingCompany ? Spinner : FiChevronDown} />
          </div>
        </Stack>
      </CompanyPopover>
    </div>
  );
};

export default PlatformCompanySelect;
